import { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { downloadXlsx } from '../utils/exportXlsx';

// Departmental activity report — a person × day grid of recorded actions over a
// week or month, exportable to Excel. Admin-only (the button only renders on the
// admin stats view and the endpoint requires the admin role).

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// offset 0 = current period, -1 = previous, …  Weeks start Monday.
function rangeFor(mode, offset) {
  const now = new Date();
  if (mode === 'week') {
    const start = new Date(now);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7) + offset * 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { start, end };
  }
  const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  return { start, end };
}

function eachDay(start, end) {
  const days = [];
  const d = new Date(start);
  while (d <= end) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

// records_jobs_history went live this day — earlier periods only show uploads
const HISTORY_EPOCH = '2026-08-05';

export default function ActivityReportModal({ dept, title, onClose }) {
  const [mode, setMode] = useState('week');
  const [offset, setOffset] = useState(0);
  const [events, setEvents] = useState(null);
  const [error, setError] = useState('');

  const { start, end } = useMemo(() => rangeFor(mode, offset), [mode, offset]);
  const from = iso(start);
  const to = iso(end);

  useEffect(() => {
    setEvents(null);
    setError('');
    api.get(`/activity/${dept}?from=${from}&to=${to}`)
      .then((res) => setEvents(res.data.events))
      .catch(() => setError("Couldn't load the report. Try again."));
  }, [dept, from, to]);

  const days = useMemo(() => eachDay(start, end), [start, end]);

  // Pivot events into people rows, day cells, and the category set actually present
  const { people, categories, cell, personTotal, dayTotal } = useMemo(() => {
    const byPersonDay = {}; // name -> day -> {total, breakdown}
    const cats = new Set();
    for (const e of events || []) {
      cats.add(e.category);
      const p = (byPersonDay[e.name] ||= {});
      const c = (p[e.day] ||= { total: 0, breakdown: {} });
      c.total += e.count;
      c.breakdown[e.category] = (c.breakdown[e.category] || 0) + e.count;
    }
    const names = Object.keys(byPersonDay).sort((a, b) => a.localeCompare(b));
    return {
      people: names,
      categories: [...cats].sort(),
      cell: (name, day) => byPersonDay[name]?.[day] || null,
      personTotal: (name) => Object.values(byPersonDay[name] || {}).reduce((s, c) => s + c.total, 0),
      dayTotal: (day) => names.reduce((s, n) => s + (byPersonDay[n]?.[day]?.total || 0), 0),
    };
  }, [events]);

  const rangeLabel =
    mode === 'week'
      ? `${start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
      : start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  function exportExcel() {
    const rows = [];
    for (const d of days) {
      const dayIso = iso(d);
      for (const name of people) {
        const c = cell(name, dayIso);
        rows.push([
          dayIso,
          d.toLocaleDateString(undefined, { weekday: 'short' }),
          name,
          ...categories.map((cat) => String(c?.breakdown[cat] || 0)),
          String(c?.total || 0),
        ]);
      }
    }
    downloadXlsx({
      filename: `${dept}-activity-${from}-to-${to}.xlsx`,
      sheetName: 'Activity',
      headers: ['Date', 'Day', 'Person', ...categories, 'Total'],
      rows,
    });
  }

  const showEpochNote = (dept === 'records' || dept === 'digs') && from < HISTORY_EPOCH;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
      <div
        className="border rounded-lg w-full max-w-4xl max-h-[85vh] flex flex-col"
        style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-0">
          <div>
            <h2 className="text-lg font-black text-white tracking-widest uppercase">{title} — Activity</h2>
            <p className="text-xs text-gray-500 mt-1">Recorded actions per person per day</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-xl leading-none px-2" title="Close">✕</button>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 p-5 flex-wrap">
          <div className="flex rounded overflow-hidden border" style={{ borderColor: '#1e3a5f' }}>
            {['week', 'month'].map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setOffset(0); }}
                className="text-xs font-bold uppercase tracking-wide px-3 py-1.5"
                style={mode === m ? { backgroundColor: '#c9a84c', color: '#0d1b2e' } : { color: '#9ca3af' }}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setOffset(offset - 1)} className="text-gray-400 hover:text-white px-2 py-1 text-sm" title={`Previous ${mode}`}>‹</button>
            <span className="text-sm text-gray-300 font-bold min-w-[11rem] text-center">{rangeLabel}</span>
            <button
              onClick={() => setOffset(Math.min(0, offset + 1))}
              disabled={offset === 0}
              className="text-gray-400 hover:text-white px-2 py-1 text-sm disabled:opacity-30"
              title={`Next ${mode}`}
            >›</button>
          </div>
          <button
            onClick={exportExcel}
            disabled={!events || people.length === 0}
            className="ml-auto text-xs px-3 py-2 rounded font-bold uppercase tracking-wide transition disabled:opacity-50"
            style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
          >
            Export to Excel
          </button>
        </div>

        {/* Body */}
        <div className="px-5 pb-5 overflow-auto">
          {error && <p className="text-red-400 text-sm">{error}</p>}
          {!error && !events && <p className="text-gray-500 text-sm">Loading…</p>}
          {events && people.length === 0 && <p className="text-gray-500 text-sm">No recorded activity in this period.</p>}

          {events && people.length > 0 && (
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="sticky left-0 text-left text-gray-400 font-bold uppercase tracking-wider pr-3 py-2" style={{ backgroundColor: '#0d1b2e' }}>Person</th>
                    {days.map((d) => {
                      const weekend = d.getDay() === 0 || d.getDay() === 6;
                      return (
                        <th key={iso(d)} className="px-1.5 py-2 text-center font-normal" style={{ color: weekend ? '#4b5563' : '#9ca3af' }}>
                          <div>{d.toLocaleDateString(undefined, { weekday: 'narrow' })}</div>
                          <div className="font-bold">{d.getDate()}</div>
                        </th>
                      );
                    })}
                    <th className="pl-3 py-2 text-right text-gray-400 font-bold uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((name) => (
                    <tr key={name} className="border-t" style={{ borderColor: '#1e3a5f' }}>
                      <td className="sticky left-0 text-gray-300 font-bold pr-3 py-1.5 whitespace-nowrap" style={{ backgroundColor: '#0d1b2e' }}>{name}</td>
                      {days.map((d) => {
                        const c = cell(name, iso(d));
                        const weekend = d.getDay() === 0 || d.getDay() === 6;
                        return (
                          <td
                            key={iso(d)}
                            className="px-1.5 py-1.5 text-center"
                            title={c ? Object.entries(c.breakdown).map(([k, v]) => `${k}: ${v}`).join('\n') : ''}
                            style={{ backgroundColor: weekend ? 'rgba(107,114,128,0.06)' : undefined }}
                          >
                            {c ? (
                              <span className="inline-block min-w-[1.5rem] rounded px-1 py-0.5 font-bold" style={{ backgroundColor: 'rgba(201,168,76,0.15)', color: '#c9a84c' }}>
                                {c.total}
                              </span>
                            ) : (
                              <span className="text-gray-700">·</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="pl-3 py-1.5 text-right text-white font-bold">{personTotal(name)}</td>
                    </tr>
                  ))}
                  <tr className="border-t" style={{ borderColor: '#1e3a5f' }}>
                    <td className="sticky left-0 text-gray-500 uppercase font-bold pr-3 py-1.5" style={{ backgroundColor: '#0d1b2e' }}>All</td>
                    {days.map((d) => (
                      <td key={iso(d)} className="px-1.5 py-1.5 text-center text-gray-400 font-bold">{dayTotal(iso(d)) || ''}</td>
                    ))}
                    <td className="pl-3 py-1.5 text-right text-white font-bold">{people.reduce((s, n) => s + personTotal(n), 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Legend + caveats */}
          {events && categories.length > 0 && (
            <p className="text-xs text-gray-500 mt-3">Counting: {categories.join(' · ')}. Hover a cell for the breakdown.</p>
          )}
          {showEpochNote && (
            <p className="text-xs text-yellow-500/80 mt-1">
              Detailed edit tracking began Aug 5, 2026 — before that, only {dept === 'records' ? 'file uploads are' : 'no dig activity is'} recorded.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
